// v3.99c.69 — Universal Share button.
// Reusable share affordance dropped on every shareable surface (article,
// strain, listing, breeder profile, trait/family page, Genetics Map).
//
// Behavior:
//   • On touch / mobile devices with `navigator.share`, tapping fires the
//     native share sheet directly (no popover) so the user can pick their
//     OS share target (AirDrop, Messages, etc).
//   • On desktop (no navigator.share or coarse-pointer=false), tapping
//     opens a small popover with: Copy link, X (Twitter), Reddit,
//     Facebook, WhatsApp, Telegram, Email.
//   • Visual variant: icon-only on mobile (≤sm) / pill ("Share" + icon)
//     on desktop. Pass `variant="icon"` or `variant="pill"` to force.
//
// All share URLs are rewritten to the canonical production domain
// (https://thecannaforge.com/...) so preview environments never leak
// preview URLs into the wild.
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Share2, Copy, Mail, Check, X as XIcon } from "lucide-react";

const CANONICAL_ORIGIN = "https://thecannaforge.com";

function toCanonicalUrl(path) {
  // Accept either an absolute URL or a path; always return canonical origin.
  try {
    if (!path) {
      const here = (typeof window !== "undefined" && window.location)
        ? window.location.pathname + window.location.search
        : "/";
      return CANONICAL_ORIGIN + here;
    }
    if (path.startsWith("http://") || path.startsWith("https://")) {
      const u = new URL(path);
      return CANONICAL_ORIGIN + u.pathname + u.search;
    }
    return CANONICAL_ORIGIN + (path.startsWith("/") ? path : "/" + path);
  } catch {
    return CANONICAL_ORIGIN + "/";
  }
}

// SVG icon set for social targets. Using inline SVG to avoid extra deps
// and to keep brand fidelity. Each is monochrome so we can color them
// via the surrounding context (var(--text-2) / hover var(--gold)).
const IconX = (props) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231ZM17.083 19.77h1.833L7.084 4.126H5.117L17.083 19.77Z"/>
  </svg>
);
const IconReddit = (props) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M22 12.07a2.18 2.18 0 0 0-3.7-1.54 10.94 10.94 0 0 0-5.85-1.85l1-4.69 3.27.7a1.55 1.55 0 1 0 .16-.97l-3.65-.78a.5.5 0 0 0-.59.39l-1.12 5.27a10.94 10.94 0 0 0-5.93 1.85 2.18 2.18 0 1 0-2.39 3.55 3.96 3.96 0 0 0-.05.6c0 3.07 3.58 5.57 8 5.57s8-2.5 8-5.57a4 4 0 0 0-.05-.6 2.18 2.18 0 0 0 .9-1.93Zm-14.5 1.43a1.5 1.5 0 1 1 1.5 1.5 1.5 1.5 0 0 1-1.5-1.5Zm8.34 4.05a4.86 4.86 0 0 1-3.34 1 4.86 4.86 0 0 1-3.34-1 .39.39 0 1 1 .55-.55 4 4 0 0 0 2.79.8 4 4 0 0 0 2.79-.8.39.39 0 0 1 .55.55Zm-.34-2.55a1.5 1.5 0 1 1 1.5-1.5 1.5 1.5 0 0 1-1.5 1.5Z"/>
  </svg>
);
const IconFacebook = (props) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12Z"/>
  </svg>
);
const IconWhatsApp = (props) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.05 4.91A10 10 0 0 0 4.06 18.14L2 22l3.94-1.05A10 10 0 1 0 19.05 4.91Zm-7.05 15.3a8.27 8.27 0 0 1-4.22-1.16l-.3-.18-2.34.62.63-2.28-.2-.31a8.32 8.32 0 1 1 6.43 3.31Zm4.55-6.21c-.25-.13-1.47-.72-1.7-.8s-.39-.13-.56.13-.65.8-.79.97-.29.19-.54.06a6.78 6.78 0 0 1-2-1.24 7.49 7.49 0 0 1-1.38-1.72c-.14-.25 0-.39.11-.51s.25-.29.37-.43a1.7 1.7 0 0 0 .25-.41.46.46 0 0 0 0-.43c-.06-.13-.56-1.36-.77-1.86s-.41-.42-.56-.43h-.48a.93.93 0 0 0-.67.31 2.83 2.83 0 0 0-.88 2.1 4.91 4.91 0 0 0 1.03 2.61 11.31 11.31 0 0 0 4.34 3.83 14.79 14.79 0 0 0 1.45.54 3.49 3.49 0 0 0 1.6.1 2.61 2.61 0 0 0 1.7-1.2 2.12 2.12 0 0 0 .15-1.2c-.07-.11-.23-.18-.48-.31Z"/>
  </svg>
);
const IconTelegram = (props) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M9.78 18.65 10.06 14.42l7.75-6.99c.34-.31-.07-.46-.52-.19l-9.57 6.04L3.6 11.95c-.89-.26-.9-.87.2-1.29l16.04-6.18c.74-.34 1.45.18 1.17 1.29l-2.73 12.87c-.19.9-.74 1.12-1.5.7l-4.13-3.05-1.99 1.93c-.23.23-.42.42-.83.42Z"/>
  </svg>
);

export default function ShareButton({
  title,
  description = "",
  path,
  variant = "auto",   // "icon" | "pill" | "auto" (icon on mobile, pill on desktop)
  testid = "share-button",
  className = "",
  size = "md",        // "sm" | "md"
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasNativeShare, setHasNativeShare] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      setHasNativeShare(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const url = toCanonicalUrl(path);
  const cleanTitle = (title || "").trim().slice(0, 140) || "CannaForge";
  // v3.99c.69 — Defensive clamp on description: social platforms (esp. X
  // at 280 chars and WhatsApp/Telegram preview crops) will silently drop
  // or reject oversized share URLs. We trim to ~140 chars + ellipsis so
  // shareText (title + " — " + desc) stays well under platform limits
  // even when callers pass a multi-paragraph hero body by mistake.
  const rawDesc = (description || "").trim().replace(/\s+/g, " ");
  const cleanDesc = rawDesc.length > 140 ? rawDesc.slice(0, 137) + "…" : rawDesc;
  const shareText = cleanDesc ? `${cleanTitle} — ${cleanDesc}` : cleanTitle;

  const copyLink = useCallback(() => {
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  }, [url]);

  const onTrigger = useCallback(async () => {
    // Prefer the native share sheet when available — desktop falls back
    // to the popover.
    if (hasNativeShare) {
      try {
        await navigator.share({ title: cleanTitle, text: shareText, url });
        return;
      } catch {
        // User dismissed — open the popover as a fallback.
      }
    }
    setOpen((o) => !o);
  }, [hasNativeShare, cleanTitle, shareText, url]);

  // Sizing presets.
  const iconBox = size === "sm" ? "w-7 h-7" : "w-9 h-9";
  const pillPad = size === "sm" ? "px-2.5 py-1" : "px-3 py-1.5";
  const iconSz = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
  const labelSz = size === "sm" ? "text-[10px]" : "text-[11px]";

  // The label is hidden on mobile (auto variant) so we can fit beside
  // other actions. Pass variant="pill" to always show the word.
  const showLabel = variant === "pill" ? "inline" : (variant === "icon" ? "hidden" : "hidden sm:inline");

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`} data-testid={testid}>
      <button
        type="button"
        onClick={onTrigger}
        className={`inline-flex items-center justify-center gap-1.5 border hover:bg-[var(--surface)] transition-colors ${pillPad}`}
        style={{
          borderColor: "var(--border-strong)",
          color: "var(--text-2)",
          borderRadius: 2,
          minHeight: 36,
        }}
        title="Share"
        aria-label="Share this page"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
      >
        <Share2 className={iconSz} />
        <span className={`${showLabel} ${labelSz} font-mono uppercase tracking-[0.18em]`}>Share</span>
      </button>

      {open && !hasNativeShare && (
        <div
          role="menu"
          className="absolute right-0 mt-1.5 z-50 min-w-[240px] bg-[var(--surface)] border border-[var(--border-strong)] shadow-xl"
          style={{ borderRadius: 2 }}
          data-testid={`${testid}-popover`}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border)]">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
              Share this page
            </span>
            <button type="button" onClick={() => setOpen(false)}
                    aria-label="Close share menu"
                    className="hover:opacity-80"
                    style={{ color: "var(--text-muted)" }}>
              <XIcon className="w-3 h-3" />
            </button>
          </div>

          <ShareItem
            icon={copied ? <Check className="w-3.5 h-3.5" style={{ color: "var(--primary-text)" }} /> : <Copy className="w-3.5 h-3.5" />}
            label={copied ? "Link copied" : "Copy link"}
            onClick={copyLink}
            testid={`${testid}-copy`}
            accent={copied ? "var(--primary-text)" : undefined}
          />
          <ShareItem
            icon={<IconX />}
            label="Share on X"
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`}
            testid={`${testid}-twitter`}
          />
          <ShareItem
            icon={<IconReddit />}
            label="Share on Reddit"
            href={`https://www.reddit.com/submit?title=${encodeURIComponent(cleanTitle)}&url=${encodeURIComponent(url)}`}
            testid={`${testid}-reddit`}
          />
          <ShareItem
            icon={<IconFacebook />}
            label="Share on Facebook"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
            testid={`${testid}-facebook`}
          />
          <ShareItem
            icon={<IconWhatsApp />}
            label="WhatsApp"
            href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + url)}`}
            testid={`${testid}-whatsapp`}
          />
          <ShareItem
            icon={<IconTelegram />}
            label="Telegram"
            href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`}
            testid={`${testid}-telegram`}
          />
          <ShareItem
            icon={<Mail className="w-3.5 h-3.5" />}
            label="Email"
            href={`mailto:?subject=${encodeURIComponent(cleanTitle)}&body=${encodeURIComponent(shareText + "\n\n" + url)}`}
            testid={`${testid}-email`}
          />

          <div className="px-3 py-2 border-t border-[var(--border)]">
            <code className="block text-[10px] truncate" style={{ color: "var(--text-muted)" }}>{url}</code>
          </div>
        </div>
      )}
    </div>
  );
}

function ShareItem({ icon, label, href, onClick, testid, accent }) {
  const cls = "w-full text-left px-3 py-2 flex items-center gap-2.5 text-xs hover:bg-[var(--bg-2)] transition-colors";
  const style = { color: accent || "var(--text-2)" };
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
         className={cls} style={style} role="menuitem" data-testid={testid}>
        {icon}
        <span>{label}</span>
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls} style={style} role="menuitem" data-testid={testid}>
      {icon}
      <span>{label}</span>
    </button>
  );
}
